/**
 * Home page bundle.
 *
 * Structure ported from the .in engine's js/Home/index.js: grabOnInit(), then
 * one Embla initialisation per carousel, then the tab switcher. The India-only
 * parts of that file (campaign gift-box animations, deals-of-the-day, the
 * hardcoded banner removal) have no International equivalent and are dropped.
 *
 * The behavioural difference worth knowing: .in fetches the other tabs'
 * coupons on click. Ours ship in the HTML — the gateway returns every section
 * with its coupons — so switching a tab is a class toggle and makes no request,
 * and every coupon on every tab is in the markup a crawler sees.
 */
import Autoplay from "embla-carousel-autoplay";
import EmblaInitialization from "@common/carousel/embla-initialization";
import BubbleHoverEffect from "@common/utils/bubble-hover-efffect";
import { checkIfInViewport } from "@common/events/checkIfInView";
import gaSC, { npga2, pbGA } from "@common/utils/ga";
import isMobile from "@common/utils/is-mobile";
import { grabOnInit, onReady } from "@core";

/** GA labels — same taxonomy as the .in home page. */
const GA = {
  TpBnrs: "Home Page Top Banners",
  TpPks: "Home Page Top Picks",
  SubBnrs: "Home Page SubBanners",
  FeatStre: "Home Page Featured Store",
  PopStre: "Home Page Popular Stores",
  TdTCO: "Todays Top Coupons And Offers Tabs",
  TdTCC: "Todays Top Coupons And Offers",
  Cats: "Home Page Popular Categories",
};

onReady(() => {
  grabOnInit();
  checkIfInViewport();

  initMainBanners();
  initTopPicks();
  initRail("#gPopularOffer", GA.SubBnrs);
  initRail("#gPopularStore", GA.PopStre);

  // .in's home GA block (js/Home/index.js), for the sections this home has.
  // Not ported: deals of the day (.g-deal), gift cards (.gh-gcard) and
  // collections — India-only sections with no International equivalent.
  pbGA(".go-bnrclk", GA.TpBnrs); // top banners
  pbGA(".go-topPicks", GA.TpPks); // top picks
  npga2(".gp-feat", GA.FeatStre); // featured store of the month
  pbGA(".go-popstore", GA.PopStre); // popular stores
  pbGA("#gPopularOffer .go-clicker", GA.SubBnrs); // sub banners
  const subForm = document.getElementById("go-sub-form");
  if (subForm) subForm.addEventListener("submit", () => gaSC("Home Page Email Subscribe"));
  document.querySelectorAll(".go-sl > li > a").forEach((link) => {
    link.addEventListener("click", () => gaSC("Footer Social Links", link.getAttribute("title") || ""));
  });
  // Popular Categories is a plain grid on the International page, not the
  // carousel .in uses — nothing to initialise.
  initTopBannerRail();
  initTopCouponTabs();
});

/** Hero: looped, autoplaying, dots. */
function initMainBanners() {
  const root = document.getElementById("mainCaru");
  if (!root) return;

  // Eager-load every slide image before init. Embla's loop clones need a real
  // src, and lazy-loading them causes a white flash on the first advance —
  // the same fix the .in home bundle applies.
  root
    .querySelectorAll("img.embla__slide__img[data-embla-lazy]")
    .forEach((img) => {
      img.src = img.getAttribute("data-embla-lazy");
      img.removeAttribute("data-embla-lazy");
    });

  if (root.querySelectorAll(".embla__slide").length === 0) return;

  EmblaInitialization({
    rootNode: root,
    options: { loop: true },
    plugins: [Autoplay({ delay: 2500 })],
    pagination: { show: true, dotsNode: root.querySelector(".embla__dots") },
    IsHomepageMainBanner: true,
    HasSliderClick: true,
    slideType: GA.TpBnrs,
  });
}

function initTopPicks() {
  const root = document.getElementById("top-picks-carousel");
  if (!root || root.querySelectorAll(".embla__slide").length === 0) return;

  EmblaInitialization({
    rootNode: root,
    options: { loop: true, align: "start" },
    // plugins: [Autoplay({ delay: 3500 })],
    pagination: { show: true, dotsNode: root.querySelector(".embla__dots") },
    IsHomepageTopPicks: true,
    HasSliderClick: true,
    slideType: GA.TpPks,
  });
}

/** A section rail with the shared .v2-embla-controls prev/next/dots block. */
function initRail(selector, slideType) {
  const root = document.querySelector(selector);
  if (!root || root.querySelectorAll(".embla__slide").length <= 1) return;

  const container = root.closest(".container");
  const controls = container && container.querySelector(".v2-embla-controls");

  EmblaInitialization({
    rootNode: root,
    options: { align: "start", containScroll: "trimSnaps" },
    navigation: {
      show: !!controls,
      prevNode: controls && controls.querySelector(".embla__prev"),
      nextNode: controls && controls.querySelector(".embla__next"),
    },
    pagination: {
      show: !!controls,
      dotsNode: controls && controls.querySelector(".embla__dots"),
    },
    slideType,
  });

  if (slideType === GA.PopStre) {
    const pStoreSlideElements = root?.querySelectorAll?.(
      ".embla__slide .go-popstore",
    );
    pStoreSlideElements?.forEach?.((element, index) => {
      if (element && element != null) {
        element?.addEventListener?.("mouseenter", function (event) {
          BubbleHoverEffect(element, event);
        });
      }
    });
  }
}

function initTopBannerRail() {
  const root = document.querySelector(".gh-top-banner.embla");
  if (!root) return;
  const controls = document.querySelector(".gh-top-banner-controls");

  EmblaInitialization({
    rootNode: root,
    options: { loop: true },
    plugins: [Autoplay({ delay: 4000 })],
    navigation: {
      show: !!controls,
      prevNode: controls && controls.querySelector(".embla__prev"),
      nextNode: controls && controls.querySelector(".embla__next"),
    },
    pagination: {
      show: !!controls,
      dotsNode: controls && controls.querySelector(".embla__dots"),
    },
    slideType: "Home Page Tab Banner",
  });
}

/**
 * Today's Top Coupons tabs.
 *
 * .in renders only the first tab and XHRs the rest in on click. Every tab's
 * coupons are already in our HTML, so switching is presentation only — no
 * request, and every coupon on every tab is in the markup a crawler sees.
 *
 * Two things make that work:
 *
 * 1. Visibility is an INLINE display, not a class. The migrated .in stylesheet
 *    sets `.g-htop-c { display: var(--_d-flex) }` with no `.show` counterpart,
 *    because .in never had a second populated panel to hide. An inline style
 *    wins over that without editing the styleset; the active panel has its
 *    inline display removed so the stylesheet's own value applies again.
 *    The markup ships inactive panels already hidden, so nothing flashes
 *    before this runs.
 *
 * 2. Embla measures zero width inside a hidden panel, so a rail is created the
 *    first time its panel is shown and reInit()ed on every show after that —
 *    the viewport may have changed while it was hidden.
 */
function initTopCouponTabs() {
  const tabs = Array.prototype.slice.call(
    document.querySelectorAll(".gh-left .ghl-each"),
  );
  const panels = Array.prototype.slice.call(
    document.querySelectorAll(".gh-right .g-htop-c"),
  );
  if (tabs.length === 0 || panels.length === 0) return;

  const show = (target) => {
    panels.forEach((panel) => {
      const active = panel.getAttribute("data-targetID") === target;
      panel.classList.toggle("show", active);
      if (active) {
        panel.style.removeProperty("display");
      } else {
        panel.style.display = "none";
      }
    });

    topCouponsEmblaInit();
  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      tabs.forEach((other) => other.classList.toggle("active", other === tab));
      gaSC(GA.TdTCO, tab.textContent.trim());
      show(tab.getAttribute("data-target"));
    });
  });

  // .in: the "see all" banner of the open Top Coupons panel, labelled with the
  // tab that owns it ($('.gh-left li').get(pos)) — ours: .ghl-each.
  const tabNodes = Array.from(document.querySelectorAll(".gh-left .ghl-each"));
  document.querySelectorAll(".gh-right .g-htop-c > a").forEach((banner) => {
    banner.addEventListener("click", () => {
      const panel = banner.parentElement;
      const pos = Array.from(panel.parentElement.children).indexOf(panel);
      const owner = tabNodes[pos];
      gaSC("Top Coupons Banner", owner ? owner.textContent.trim() : "");
    });
  });

  // The first tab is already visible in the markup.
  topCouponsEmblaInit();
}

/**
 * Carousel + controls for the visible Today's Top Coupons panel.
 *
 * Ported from .in's `topCouponsEmblaInitialization`. The controls are BUILT
 * HERE rather than in the markup, and that is deliberate: whether a panel
 * needs them depends on how many coupons it has (>6 becomes a rail, ≤6 is a
 * plain grid), so a tab switch can go from needing them to not. The section
 * carries `data-embla-controls-show` for the stylesheet, exactly as .in does.
 *
 * Runs on load and after every tab switch.
 */
function topCouponsEmblaInit() {
  /** The live instance, so a tab switch can tear the previous one down. */
  let topCouponsEmbla = null;
  const section = document.querySelector(".g-top-c");
  if (!section) return;

  const panel = section.querySelector(".gh-right > .g-htop-c.show");
  const rail = panel && panel.querySelector('.gh-ec-w[data-id="gh-topc"]');
  const hasEmbla = !!rail;

  section.setAttribute("data-embla-controls-show", hasEmbla ? "true" : "false");

  // Drop the outgoing carousel before building the next one. .in leaves the
  // old instance attached, which stacks a fresh one on the same node every
  // time you return to a tab.
  if (topCouponsEmbla) {
    topCouponsEmbla.destroy();
    topCouponsEmbla = null;
  }

  let controls = section.querySelector(".gh-top-c-controls");

  if (!hasEmbla) {
    if (controls) controls.remove();
    return;
  }

  /** The prev / dots / next block, identical to the one in the .in original. */
  const TOP_COUPON_CONTROLS = `<button type="button" class="embla__prev embla__btn" role="button" title="Previous Slide" aria-label="Go to Previous Slide"></button>
<div class="embla__dots"></div>
<button type="button" class="embla__next embla__btn" role="button" title="Next Slide" aria-label="Go to Next Slide"></button>`;

  if (!controls) {
    controls = document.createElement("div");
    controls.className = "v2-embla-controls gh-top-c-controls";
    controls.innerHTML = TOP_COUPON_CONTROLS;
    section
      .querySelector(".container")
      .insertBefore(controls, section.querySelector(".gh-left"));
  }

  normalizeTopCouponsForMobile(rail);

  topCouponsEmbla = EmblaInitialization({
    rootNode: rail,
    options: {
      containScroll: "trimSnaps",
      align: "start",
      slidesToScroll: isMobile() ? 2 : 1,
    },
    navigation: {
      show: true,
      prevNode: controls.querySelector(".embla__prev"),
      nextNode: controls.querySelector(".embla__next"),
      disable: true,
    },
    pagination: {
      show: true,
      dotsNode: controls.querySelector(".embla__dots"),
    },
    slideType: GA.TdTCC,
  });
}

/**
 * On mobile the cards page one at a time, not six at a time — so the
 * six-per-slide grouping the markup ships is flattened into one card per
 * slide. Idempotent via data-mobile-normalized, since a tab can be revisited.
 */
function normalizeTopCouponsForMobile(rootNode) {
  if (!rootNode || !isMobile()) return;

  const container = rootNode.querySelector(".embla__container");
  if (
    !container ||
    rootNode.getAttribute("data-mobile-normalized") === "true"
  ) {
    return;
  }

  const groups = [...container.querySelectorAll(":scope > .embla__slide")];
  if (groups.length === 0) return;

  const flattened = [];
  groups.forEach((group, groupIndex) => {
    [...group.children]
      .filter((child) => child.classList.contains("gh-ec"))
      .forEach((card, cardIndex) => {
        const slide = document.createElement("div");
        slide.className = `embla__slide${groupIndex === 0 && cardIndex === 0 ? " is-active" : ""}`;
        slide.appendChild(card);
        flattened.push(slide);
      });
  });

  container.innerHTML = "";
  flattened.forEach((slide) => container.appendChild(slide));
  rootNode.setAttribute("data-mobile-normalized", "true");
}
